import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItem, ListItemAvatar, ListItemText, styled } from '@mui/material';
import classNames from 'classnames';

import { Avatar } from '@components/base';
import useGenericDataContext from '@hooks/useGenericDataContext';
import { DetailedTitle, ScoreRatingProvider } from '@models/Title';

import Section from './Section';

const StyledAvatar = styled(Avatar)`
  &.padded {
    background: ${({ theme }) => theme.palette.common.white};
    padding: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const scoreRatingProviderScala: Record<ScoreRatingProvider, number> = {
  dagbladet: 6,
  imdb: 10,
  rottenTomatoes: 10,
  vg: 6,
};

const getScoreRatingRatio = (provider: ScoreRatingProvider, rating: number) =>
  `${rating} / ${provider in scoreRatingProviderScala ? scoreRatingProviderScala[provider] : 6}`;

const ScoreRating: FC = () => {
  const { t } = useTranslation();
  const { data: title } = useGenericDataContext<DetailedTitle>();

  if (!title) return null;

  const scoreRating = Object.entries(title.scoreRating || {}).filter(([, value]) => !!value) as [
    ScoreRatingProvider,
    number,
  ][];

  return (
    <Section category='scoreRating' empty={!title.scoreRating || scoreRating.length === 0}>
      {scoreRating.map(([provider, value], index) => {
        const ratingProvider = t('titleDetails.scoreRatingProvider', { context: provider });

        const getAvatarSrc = () => {
          switch (provider) {
            case 'imdb':
              return '/assets/imdb.png';
            case 'vg':
              return '/assets/vg.png';
            case 'dagbladet':
              return '/assets/dagbladet.png';
            case 'rottenTomatoes':
              return value < 6 ? '/assets/rt-bad.svg' : '/assets/rt-good.svg';
            default:
              return;
          }
        };

        const avatarSrc = getAvatarSrc();

        return (
          <ListItem dense divider={index < scoreRating.length - 1} key={provider}>
            {avatarSrc && (
              <ListItemAvatar>
                <StyledAvatar
                  alt={`${ratingProvider} logo`}
                  className={classNames({ padded: avatarSrc.endsWith('.svg') })}
                  size='large'
                  src={avatarSrc}
                />
              </ListItemAvatar>
            )}

            <ListItemText
              primary={getScoreRatingRatio(provider, value)}
              secondary={ratingProvider}
            />
          </ListItem>
        );
      })}
    </Section>
  );
};

export default ScoreRating;
